import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import { UserContext } from '../../context/UserContext';
import './FestivalsPage.css';
import PurplArrowPrev from '../../img/icons/PurplArrowPrev.png';
import PurplArrowNext from '../../img/icons/PurplArrowNext.png';

const FestivalsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [allEvents, setAllEvents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  // Tab is only set via homepage navigation (not manually selectable)
  const [currentTab, setCurrentTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'past') return 'past';
    if (tabParam === 'upcoming') return 'upcoming';
    return 'all';
  });
  const [loading, setLoading] = useState(true);
  const eventsPerPage = 9;

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  // Pre-populate dance styles from user profile when logged in
  const [selectedStyles, setSelectedStyles] = useState(() => {
    if (user?.dances?.length) {
      return user.dances.map(d => d.style).filter(Boolean);
    }
    return [];
  });
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedRating, setSelectedRating] = useState('all');
  // Initialize organizer filter from URL
  const [selectedOrganizer, setSelectedOrganizer] = useState(() => {
    return searchParams.get('organizer') || '';
  });
  // Initialize artist filter from URL
  const [selectedArtist, setSelectedArtist] = useState(() => {
    return searchParams.get('artist') || '';
  });
  // Initialize event series filter from URL
  const [selectedSeries, setSelectedSeries] = useState(() => {
    return searchParams.get('series') || '';
  });

  // Sync URL params → state when navigating to this page from elsewhere
  // (useState initializers only run on first mount, not on subsequent navigations)
  useEffect(() => {
    const orgParam = searchParams.get('organizer') || '';
    const artistParam = searchParams.get('artist') || '';
    const seriesParam = searchParams.get('series') || '';
    const tabParam = searchParams.get('tab');
    if (orgParam !== selectedOrganizer) setSelectedOrganizer(orgParam);
    if (artistParam !== selectedArtist) setSelectedArtist(artistParam);
    if (seriesParam !== selectedSeries) setSelectedSeries(seriesParam);
    if (tabParam === 'past' && currentTab !== 'past') setCurrentTab('past');
    else if (tabParam === 'upcoming' && currentTab !== 'upcoming') setCurrentTab('upcoming');
    else if (!tabParam && currentTab !== 'all') setCurrentTab('all');
  }, [searchParams]);

  // Dance style search state
  const [styleSearchInput, setStyleSearchInput] = useState('');
  const [styleSuggestions, setStyleSuggestions] = useState([]);
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);
  const styleSearchTimeout = useRef(null);
  const styleDropdownRef = useRef(null);
  const filterSectionRef = useRef(null);

  // Info popup state
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  // Mobile "More Filters" toggle
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  // Common dance styles for quick selection
  const quickStyleOptions = [
    'Bachata', 'Salsa', 'Kizomba', 'Zouk', 'West Coast Swing', 'Tango'
  ];

  // Month options
  const monthOptions = [
    { label: 'All Months', value: 'all' },
    { label: 'January', value: 0 },
    { label: 'February', value: 1 },
    { label: 'March', value: 2 },
    { label: 'April', value: 3 },
    { label: 'May', value: 4 },
    { label: 'June', value: 5 },
    { label: 'July', value: 6 },
    { label: 'August', value: 7 },
    { label: 'September', value: 8 },
    { label: 'October', value: 9 },
    { label: 'November', value: 10 },
    { label: 'December', value: 11 },
  ];

  // Rating filter options (for past events)
  const ratingOptions = [
    { label: 'Any Rating', value: 'all' },
    { label: '4+ Stars', value: 4 },
    { label: '3+ Stars', value: 3 },
    { label: '2+ Stars', value: 2 },
  ];

  // Generate year options dynamically
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [{ label: 'All Years', value: 'all' }];

    // Show future years + past years
    for (let year = currentYear + 2; year >= currentYear - 5; year--) {
      options.push({ label: year.toString(), value: year });
    }
    return options;
  };

  const yearOptions = getYearOptions();

  // Scroll to filters when arriving with organizer/artist/series param, otherwise scroll to top
  useEffect(() => {
    const hasFilterParam = searchParams.get('organizer') || searchParams.get('artist') || searchParams.get('series');
    if (hasFilterParam && filterSectionRef.current) {
      // Small delay to let the page render first
      setTimeout(() => {
        filterSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [searchParams]);

  // Close style dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (styleDropdownRef.current && !styleDropdownRef.current.contains(event.target)) {
        setShowStyleDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load events when tab changes
  useEffect(() => {
    loadEvents();
  }, [currentTab]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      if (currentTab === 'all') {
        // Fetch both future and past events
        const [futureRes, pastRes] = await Promise.all([
          fetch(`${API_BASE}/events/future-events?limit=100`),
          fetch(`${API_BASE}/events/past-events?limit=100`),
        ]);
        const futureData = await futureRes.json();
        const pastData = await pastRes.json();
        const combined = [
          ...(futureData.success && futureData.data ? futureData.data : []),
          ...(pastData.success && pastData.data ? pastData.data : []),
        ];
        // Sort by start date descending (upcoming first)
        combined.sort((a, b) => new Date(b.startDateTime) - new Date(a.startDateTime));
        setAllEvents(combined);
      } else {
        const endpoint = currentTab === 'upcoming' ? 'future-events' : 'past-events';
        const response = await fetch(`${API_BASE}/events/${endpoint}?limit=100`);
        const data = await response.json();
        setAllEvents(data.success && data.data ? data.data : []);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      setAllEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter events based on all selected criteria
  const getFilteredEvents = () => {
    return allEvents.filter(event => {
      // Text search filter
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const matchesTitle = event.title?.toLowerCase().includes(searchLower);
        const matchesLocation = event.city?.toLowerCase().includes(searchLower) ||
                                event.country?.toLowerCase().includes(searchLower) ||
                                event.location?.toLowerCase().includes(searchLower);
        if (!matchesTitle && !matchesLocation) return false;
      }

      // Month filter
      if (selectedMonth !== 'all') {
        const eventDate = new Date(event.startDateTime);
        if (eventDate.getMonth() !== selectedMonth) return false;
      }

      // Year filter
      if (selectedYear !== 'all') {
        const eventDate = new Date(event.startDateTime);
        if (eventDate.getFullYear() !== selectedYear) return false;
      }

      // Dance styles filter (multiple selection)
      if (selectedStyles.length > 0) {
        const eventStyles = event.category || [];
        const hasMatchingStyle = selectedStyles.some(style => eventStyles.includes(style));
        if (!hasMatchingStyle) return false;
      }

      // Location filter (city or country)
      if (selectedLocation) {
        const locationLower = selectedLocation.toLowerCase();
        const matchesCity = event.city?.toLowerCase().includes(locationLower);
        const matchesCountry = event.country?.toLowerCase().includes(locationLower);
        if (!matchesCity && !matchesCountry) return false;
      }

      // Rating filter (for past events only)
      if (selectedRating !== 'all' && currentTab === 'past') {
        if (!event.averageRating || event.averageRating < selectedRating) return false;
      }

      // Organizer filter
      if (selectedOrganizer) {
        const organizerLower = selectedOrganizer.toLowerCase();
        const matchesOrganizers = event.organizers?.some(o => o.toLowerCase().includes(organizerLower));
        const matchesOrganizer = event.organizer?.toLowerCase().includes(organizerLower);
        if (!matchesOrganizers && !matchesOrganizer) return false;
      }

      // Artist filter
      if (selectedArtist) {
        const artistLower = selectedArtist.toLowerCase();
        const matchesArtists = event.artists?.some(a => a.toLowerCase().includes(artistLower));
        const matchesArtist = event.artist?.toLowerCase().includes(artistLower);
        if (!matchesArtists && !matchesArtist) return false;
      }

      // Event series filter
      if (selectedSeries) {
        const seriesLower = selectedSeries.toLowerCase();
        if (!event.eventSeries?.toLowerCase().includes(seriesLower)) return false;
      }

      return true;
    });
  };

  // Get events for current page
  const getPaginatedEvents = () => {
    const filtered = getFilteredEvents();
    const start = (currentPage - 1) * eventsPerPage;
    const end = start + eventsPerPage;
    return filtered.slice(start, end);
  };

  const filteredEvents = getFilteredEvents();
  const maxPage = Math.ceil(filteredEvents.length / eventsPerPage);
  const eventsToShow = getPaginatedEvents();

  // Handle style toggle
  const handleStyleToggle = (style) => {
    setSelectedStyles(prev =>
      prev.includes(style)
        ? prev.filter(s => s !== style)
        : [...prev, style]
    );
    setCurrentPage(1);
  };

  // Handle dance style search input
  const handleStyleSearchInput = (e) => {
    const value = e.target.value;
    setStyleSearchInput(value);

    if (styleSearchTimeout.current) clearTimeout(styleSearchTimeout.current);

    if (value.trim().length < 1) {
      setStyleSuggestions([]);
      return;
    }

    styleSearchTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/dance-styles?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) {
          // Filter out already selected styles
          const filtered = data.data.filter(item => !selectedStyles.includes(item.name));
          setStyleSuggestions(filtered);
        }
      } catch (error) {
        console.error('Error fetching dance styles:', error);
      }
    }, 200);
  };

  // Add style from search
  const addStyleFromSearch = (style) => {
    if (style && !selectedStyles.includes(style)) {
      setSelectedStyles([...selectedStyles, style]);
      setStyleSearchInput('');
      setStyleSuggestions([]);
      setShowStyleDropdown(false);
      setCurrentPage(1);
    }
  };

  // Remove style
  const removeStyle = (style) => {
    setSelectedStyles(selectedStyles.filter(s => s !== style));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchText('');
    setSelectedMonth('all');
    setSelectedYear('all');
    setSelectedStyles([]);
    setSelectedLocation('');
    setSelectedRating('all');
    setSelectedOrganizer('');
    setSelectedArtist('');
    setSelectedSeries('');
    setStyleSearchInput('');
    setCurrentPage(1);
    // Remove organizer, artist, and series from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('organizer');
    newParams.delete('artist');
    newParams.delete('series');
    setSearchParams(newParams);
  };

  // Check if any filter is active
  const hasActiveFilters = searchText || selectedMonth !== 'all' || selectedYear !== 'all' || selectedStyles.length > 0 || selectedLocation || selectedRating !== 'all' || selectedOrganizer || selectedArtist || selectedSeries;

  // Handle event card click
  const handleEventClick = (eventId) => {
    navigate(`/event/${eventId}`);
  };

  // Currency symbol helper
  const getCurrencySymbol = (code) => {
    const symbols = {
      'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF',
      'PLN': 'zł', 'CZK': 'Kč', 'SEK': 'kr', 'NOK': 'kr',
      'DKK': 'kr', 'CAD': 'C$', 'AUD': 'A$', 'BRL': 'R$', 'MXN': 'MX$'
    };
    return symbols[code] || code || '€';
  };

  // Format price with currency
  const formatPrice = (price, currency) => {
    if (!price) return 'TBA';
    const symbol = getCurrencySymbol(currency);
    // If price already contains currency symbol or text, return as is
    if (/[€$£]/.test(price) || /free/i.test(price)) return price;
    return `${symbol}${price}`;
  };

  // Render star rating for past events
  const renderStarRating = (rating) => {
    if (!rating) return null;
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 === 0.5;

    return (
      <div className="event-card-star-ribbon">
        {[...Array(5)].map((_, i) => {
          if (i < fullStars) {
            return <FaStar key={i} className="star-filled" />;
          } else if (i === fullStars && hasHalf) {
            return <FaStarHalfAlt key={i} className="star-filled" />;
          } else {
            return <FaRegStar key={i} className="star-empty" />;
          }
        })}
      </div>
    );
  };

  // Render event card
  const renderEventCard = (event) => {
    const startDate = new Date(event.startDateTime);
    const month = startDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const day = startDate.getDate();

    const styles = event.category || [];
    const location = [event.city, event.country].filter(Boolean).join(', ') || event.location || 'TBA';

    // Show rating for past events (works in both 'past' and 'all' tabs)
    const isPastEvent = new Date(event.endDateTime || event.startDateTime) < new Date();
    const averageRating = isPastEvent && event.averageRating ? event.averageRating : null;

    return (
      <article
        key={event._id}
        className={`event-card ${isPastEvent ? 'event-card--past' : 'event-card--future'}`}
        onClick={() => handleEventClick(event._id)}
      >
        <div
          className="event-card-image"
          style={event.eventImage ? {
            backgroundImage: `url('${event.eventImage}')`,
          } : {}}
        >
          {/* Coming Up badge for future events */}
          {!isPastEvent && (
            <span className="event-card-coming-up">Coming Up</span>
          )}
          {/* Star rating ribbon for past events - top left */}
          {isPastEvent && averageRating && renderStarRating(averageRating)}

          <div className="event-card-date-badge">
            <div className="month">{month}</div>
            <div className="day">{day}</div>
            <div className="year">{startDate.getFullYear()}</div>
          </div>
          <div className="event-card-styles">
            {styles.slice(0, 3).map((style, idx) => (
              <span key={idx} className="event-style-badge">{style}</span>
            ))}
          </div>
        </div>
        <div className="event-card-content">
          <h3 className="event-card-title">{event.title}</h3>
          <div className="event-card-location">
            <i className="bi bi-geo-alt"></i>
            <span>{location}</span>
          </div>
          {isPastEvent && event.highlightTags?.length > 0 && (
            <div className="event-card-tags">
              {event.highlightTags.slice(0, 3).map((tag, idx) => (
                <span
                  key={idx}
                  className={`event-card-tag event-card-tag--${tag.key}`}
                  title={tag.tooltip}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          )}
          <div className="event-card-meta">
            <div className="event-card-price">{formatPrice(event.price, event.currency)}</div>
            <div className="event-card-attendance">
              {(event.peopleResponse || event.fbRespondents > 0) && (
                <span className="attendance-badge attendance-badge--fb" title="Facebook respondents">
                  <i className="bi bi-facebook"></i> {event.peopleResponse || event.fbRespondents}
                </span>
              )}
              {event.confirmedAttendees?.length > 0 && (
                <span className="attendance-badge attendance-badge--mnm" title="Confirmed on Move n Muse">
                  <i className="bi bi-people-fill"></i> {event.confirmedAttendees.length}
                </span>
              )}
            </div>
          </div>
        </div>
      </article>
    );
  };

  // Render empty state
  const renderEmptyState = () => {
    const messages = {
      upcoming: 'No upcoming events found. Be the first to add one!',
      past: 'No past events found.',
      all: 'No events found matching your filters.',
    };
    const message = messages[currentTab] || messages.all;

    return (
      <div className="empty-state">
        <i className="bi bi-calendar-x"></i>
        <h3>No Events Found</h3>
        <p>{message}</p>
        <Link to="/events/import" className="btn-add-event">
          <i className="bi bi-plus-circle"></i> Add an Event
        </Link>
      </div>
    );
  };

  return (
    <div className="festivals-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <h1 className="hero-title">Dance <span className="highlight-yellow">Events</span></h1>
          <p className="hero-subtitle">Discover dance events around the world - find your next adventure!</p>
        </div>
        {/* Tab indicator when arriving from homepage */}
        {currentTab !== 'all' && (
          <div className="event-tabs-wrapper">
            <div className="event-tabs">
              <span className="event-tab active">
                {currentTab === 'upcoming' ? 'Upcoming Events' : 'Past Events'}
              </span>
              <button
                className="event-tab"
                onClick={() => { setCurrentTab('all'); setSearchParams({}); setCurrentPage(1); }}
              >
                Show All
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Filter Section */}
      <section className="filter-section" ref={filterSectionRef}>
        <div className="container">
          {/* More Filters toggle - visible on mobile only */}
          <button
            className="more-filters-toggle"
            onClick={() => setShowMoreFilters(!showMoreFilters)}
          >
            <i className={`bi ${showMoreFilters ? 'bi-funnel-fill' : 'bi-funnel'}`}></i>
            {showMoreFilters ? 'Hide Filters' : 'More Filters'}
            {hasActiveFilters && <span className="active-filter-dot"></span>}
          </button>

          <div className={`filter-bar-collapsible${showMoreFilters ? ' show' : ''}`}>
            <div className="filter-bar">
              {/* Text Search */}
              <div className="filter-group filter-search">
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
                  className="filter-input"
                />
                <i className="bi bi-search"></i>
              </div>

              {/* Year Filter */}
              <div className="filter-group">
                <select
                  value={selectedYear}
                  onChange={(e) => { setSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value)); setCurrentPage(1); }}
                  className="filter-select"
                >
                  {yearOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Month Filter */}
              <div className="filter-group">
                <select
                  value={selectedMonth}
                  onChange={(e) => { setSelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value)); setCurrentPage(1); }}
                  className="filter-select"
                >
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Location Filter */}
              <div className="filter-group filter-group-location">
                <input
                  type="text"
                  placeholder="City or Country"
                  value={selectedLocation}
                  onChange={(e) => { setSelectedLocation(e.target.value); setCurrentPage(1); }}
                  className="filter-input filter-location"
                />
              </div>

              {/* Rating Filter (only for past events) */}
              {currentTab === 'past' && (
                <div className="filter-group">
                  <select
                    value={selectedRating}
                    onChange={(e) => { setSelectedRating(e.target.value === 'all' ? 'all' : parseInt(e.target.value)); setCurrentPage(1); }}
                    className="filter-select"
                  >
                    {ratingOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button className="filter-clear-btn" onClick={clearAllFilters}>
                  <i className="bi bi-x-circle"></i> Clear
                </button>
              )}
            </div>

            {/* Organizer filter indicator */}
            {selectedOrganizer && (
              <div className="organizer-filter-indicator">
                <span className="filter-label">Organizer:</span>
                <span className="selected-style-chip organizer-chip">
                  <i className="bi bi-person"></i> {selectedOrganizer}
                  <button onClick={() => { setSelectedOrganizer(''); const newParams = new URLSearchParams(searchParams); newParams.delete('organizer'); setSearchParams(newParams); }} className="remove-style-btn">&times;</button>
                </span>
              </div>
            )}

            {/* Artist filter indicator */}
            {selectedArtist && (
              <div className="organizer-filter-indicator">
                <span className="filter-label">Artist:</span>
                <span className="selected-style-chip organizer-chip">
                  <i className="bi bi-star"></i> {selectedArtist}
                  <button onClick={() => { setSelectedArtist(''); const newParams = new URLSearchParams(searchParams); newParams.delete('artist'); setSearchParams(newParams); }} className="remove-style-btn">&times;</button>
                </span>
              </div>
            )}

            {/* Event series filter indicator */}
            {selectedSeries && (
              <div className="organizer-filter-indicator">
                <span className="filter-label">Event Series:</span>
                <span className="selected-style-chip organizer-chip">
                  <i className="bi bi-collection"></i> {selectedSeries}
                  <button onClick={() => { setSelectedSeries(''); const newParams = new URLSearchParams(searchParams); newParams.delete('series'); setSearchParams(newParams); }} className="remove-style-btn">&times;</button>
                </span>
              </div>
            )}
          </div>

          {/* Dance Styles - Searchable with quick options (always visible) */}
          <div className="filter-styles-row">
            <span className="filter-label">Dance Styles:</span>

            {/* Selected styles as removable chips */}
            {selectedStyles.length > 0 && (
              <div className="selected-styles">
                {selectedStyles.map(style => (
                  <span key={style} className="selected-style-chip">
                    {style}
                    <button onClick={() => removeStyle(style)} className="remove-style-btn">&times;</button>
                  </span>
                ))}
              </div>
            )}

            {/* Style search input */}
            <div className="style-search-wrapper" ref={styleDropdownRef}>
              <input
                type="search"
                placeholder="Search dance style..."
                value={styleSearchInput}
                onChange={handleStyleSearchInput}
                onFocus={() => setShowStyleDropdown(true)}
                className="style-search-input"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                name="dance-style-search-field"
                aria-autocomplete="none"
                data-lpignore="true"
                data-form-type="other"
              />
              {showStyleDropdown && (
                <div className="style-suggestions">
                  {/* Quick options */}
                  {styleSearchInput.length === 0 && (
                    <div className="quick-styles">
                      {quickStyleOptions.filter(s => !selectedStyles.includes(s)).map(style => (
                        <button
                          key={style}
                          className="quick-style-btn"
                          onClick={() => addStyleFromSearch(style)}
                        >
                          {style}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Search suggestions */}
                  {styleSuggestions.length > 0 && (
                    <div className="style-suggestions-list">
                      {styleSuggestions.map((item, idx) => (
                        <div
                          key={idx}
                          className="style-suggestion-item"
                          onClick={() => addStyleFromSearch(item.name)}
                        >
                          <span>{item.name}</span>
                          <span className="style-count">{item.count} events</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Results count + Can't find prompt */}
          <div className="filter-results-row">
            <div className="filter-results-count">
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
            </div>
            <button
              className="cant-find-btn"
              onClick={() => setShowInfoPopup(true)}
            >
              <i className="bi bi-question-circle"></i> Can't find your event?
            </button>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="container">
        <div className="events-grid">
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : eventsToShow.length > 0 ? (
            eventsToShow.map(event => renderEventCard(event))
          ) : (
            renderEmptyState()
          )}
        </div>

        {/* Navigation Arrows */}
        {!loading && eventsToShow.length > 0 && (
          <div className="nav-arrows">
            <button
              className="nav-arrow"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              <img src={PurplArrowPrev} alt="Previous" />
            </button>
            <button
              className="nav-arrow"
              onClick={() => setCurrentPage(p => Math.min(maxPage, p + 1))}
              disabled={currentPage >= maxPage}
            >
              <img src={PurplArrowNext} alt="Next" />
            </button>
          </div>
        )}
      </section>

      {/* Floating Add Button */}
      <Link to="/events/import" className="floating-add-btn" title="Add an event">
        <i className="bi bi-plus"></i>
      </Link>

      {/* Can't Find Event Popup */}
      {showInfoPopup && (
        <div className="info-popup-overlay" onClick={() => setShowInfoPopup(false)}>
          <div className="info-popup" onClick={(e) => e.stopPropagation()}>
            <button className="info-popup-close" onClick={() => setShowInfoPopup(false)}>
              <i className="bi bi-x-lg"></i>
            </button>
            <div className="info-popup-icon">
              <i className="bi bi-calendar-plus"></i>
            </div>
            <h3>Can't find your event?</h3>
            <p>Be the one to create it! Share dance events with the Move n Muse community.</p>
            <Link to="/events/import" className="info-popup-btn" onClick={() => setShowInfoPopup(false)}>
              <i className="bi bi-plus-circle"></i> Add Your Event
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default FestivalsPage;
